import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTrackVisibility } from 'react-intersection-observer-hook';
import { Link } from 'react-router';

import { Button, Loader } from '@any-ui-react/core';
import * as Avatar from '@radix-ui/react-avatar';
import classNames from 'classnames';

import { ImagePlaceholderIcon } from '@anyx/shared-icons';


import { RichMenuItem } from '../types';

interface RichMenuSelectorListProps {
  richMenuList: RichMenuItem[];
  selectedRichMenuId?: string;
  onSelect: (richMenu: RichMenuItem) => void;
  isLoadingMore?: boolean;
  to: string;
  fetchMoreRichMenus?: () => void;
  loading?: boolean;
  onClose?: () => void;
}

export const RichMenuSelectorList = ({
  selectedRichMenuId,
  richMenuList,
  onSelect,
  isLoadingMore,
  to,
  fetchMoreRichMenus,
  loading,
  onClose,
}: RichMenuSelectorListProps) => {
  const { t } = useTranslation(['crm', 'shared']);

  const [ref, { isVisible, rootRef }] = useTrackVisibility();

  useEffect(() => {
    if (isVisible && !isLoadingMore) {
      fetchMoreRichMenus?.();
    }
  }, [isVisible, fetchMoreRichMenus, isLoadingMore]);

  return (
    <div>
      {loading ? (
        <div className="flex h-20 w-full items-center justify-center">
          <Loader size="sm" />
        </div>
      ) : richMenuList.length > 0 ? (
        <ul
          ref={rootRef}
          className="border-gray-1 divide-gray-1 max-h-60 divide-y divide-solid overflow-auto border-y pr-2"
        >
          {richMenuList.map(({ imageUrl, title, id }, index) => {
            const isSelected = id === selectedRichMenuId;
            return (
              <li
                className="flex h-20 items-center gap-x-4"
                key={id}
                ref={richMenuList.length - 1 === index ? ref : undefined}
              >
                <div className="bg-gray-0 flex aspect-5/3 w-24 shrink-0 items-center justify-center overflow-hidden rounded">
                  <Avatar.Root asChild>
                    <div className="overflow-hidden">
                      <Avatar.Image
                        src={imageUrl}
                        alt={title ?? ''}
                        className="h-full w-full object-cover"
                      />
                      <Avatar.Fallback delayMs={1000}>
                        <div className="bg-gray-1 flex h-full w-full items-center justify-center rounded">
                          <ImagePlaceholderIcon className="text-gray-7 h-8 w-8" />
                        </div>
                      </Avatar.Fallback>
                    </div>
                  </Avatar.Root>
                </div>
                <p className="w-full truncate">{title}</p>
                <Button
                  variant="default"
                  type="button"
                  className={classNames('ml-auto', {
                    'disabled:border-blue-6 disabled:text-blue-6 disabled:bg-blue-2': isSelected,
                  })}
                  disabled={isSelected}
                  onClick={() => onSelect({ id, title, imageUrl })}
                >
                  {t('shared.button.select', { ns: 'shared' })}
                </Button>
              </li>
            );
          })}

          {isLoadingMore && (
            <div className="my-4 flex justify-center">
              <Loader size="sm" />
            </div>
          )}
        </ul>
      ) : (
        <div className="flex flex-col items-center gap-y-4 border-y py-8">
          <p>{t('crm.richMenuSelector.dialog.selectRichMenu.noRichMenu', { ns: 'crm' })}</p>
          <Button component={Link} variant="default" target="_blank" to={to} onClick={onClose}>
            {t('crm.richMenuSelector.dialog.selectRichMenu.createRichMenu', { ns: 'crm' })}
          </Button>
        </div>
      )}
    </div>
  );
};
